import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { CheckCircle, Loader2, AlertCircle, Users, Trophy, Tag, ArrowLeft } from "lucide-react";
import { useClaimAccessCode } from '@/hooks/useClaimAccessCode';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';

interface AccessCodeEntryProps {
  onBack: () => void;
  onSuccess?: () => void;
  eventId?: string;
}

const AccessCodeEntry = ({ onBack, onSuccess, eventId }: AccessCodeEntryProps) => {
  const [code, setCode] = useState('');
  const { verifyCode, claimCode, codeData, isVerifying, isClaiming, error, clearError } = useClaimAccessCode();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useLanguage();

  const handleCodeChange = (value: string) => {
    setCode(value.toUpperCase());
    clearError();
  };

  const handleVerify = async () => {
    if (code.length === 6) {
      await verifyCode(code);
    }
  };

  const handleClaim = async () => {
    if (codeData) {
      const success = await claimCode(codeData.tokenId);
      if (success) {
        // Navigate to the event page
        if (onSuccess) {
          onSuccess();
        } else {
          navigate(`/events/${codeData.eventId}`);
        }
      }
    }
  };

  const getModalityDisplay = (modality: string | null): string => {
    switch (modality?.toLowerCase()) {
      case 'singles':
        return 'Singles';
      case 'dobles':
      case 'doubles':
        return 'Dobles';
      default:
        return modality || 'Dobles';
    }
  };

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
      <CardHeader className={isMobile ? 'p-4' : ''}>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onBack}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <CardTitle className={`${isMobile ? 'text-lg' : 'text-xl'} text-slate-800`}>
            {t('registration.accessCodeEntry.title')}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className={`space-y-6 ${isMobile ? 'p-4 pt-0' : ''}`}>
        {!codeData ? (
          // Code Entry View
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <p className="text-sm text-slate-600">
                {t('registration.accessCodeEntry.description')}
              </p>
            </div>

            {/* OTP Input */}
            <div className="flex justify-center">
            <InputOTP
                maxLength={6}
                value={code}
                onChange={handleCodeChange}
                pattern="^[A-Za-z0-9]+$"
                inputMode="text"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="w-12 h-14 text-xl font-bold uppercase" />
                  <InputOTPSlot index={1} className="w-12 h-14 text-xl font-bold uppercase" />
                  <InputOTPSlot index={2} className="w-12 h-14 text-xl font-bold uppercase" />
                  <InputOTPSlot index={3} className="w-12 h-14 text-xl font-bold uppercase" />
                  <InputOTPSlot index={4} className="w-12 h-14 text-xl font-bold uppercase" />
                  <InputOTPSlot index={5} className="w-12 h-14 text-xl font-bold uppercase" />
                </InputOTPGroup>
              </InputOTP>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Verify Button */}
            <Button
              onClick={handleVerify}
              disabled={code.length !== 6 || isVerifying}
              className="w-full bg-gradient-to-r from-primary to-primary/70 hover:from-primary/90 hover:to-primary/60 text-primary-foreground font-medium"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('registration.accessCodeEntry.verifying')}
                </>
              ) : (
                t('registration.accessCodeEntry.verifyButton')
              )}
            </Button>

            <p className="text-xs text-center text-slate-500">
              {t('registration.accessCodeEntry.footerNote')}
            </p>
          </div>
        ) : (
          // Registration Found View
          <div className="space-y-6">
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
              <span className="font-medium">{t('registration.accessCodeEntry.registrationFound')}</span>
            </div>

            {/* Registration Details */}
            <div className="space-y-4 bg-slate-50 rounded-lg p-4">
              {/* Event Title */}
              <div className="flex items-start gap-3">
                <Trophy className="h-5 w-5 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">{t('registration.accessCodeEntry.event')}</p>
                  <p className="font-semibold text-slate-800">{codeData.eventTitle}</p>
                </div>
              </div>

              {/* Category */}
              {(codeData.category || codeData.subcategory) && (
                <div className="flex items-start gap-3">
                  <Tag className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">{t('registration.accessCodeEntry.category')}</p>
                    <div className="flex items-center gap-2">
                      {codeData.category && (
                        <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">
                          {codeData.category}
                        </Badge>
                      )}
                      {codeData.subcategory && codeData.subcategory !== codeData.category && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          {codeData.subcategory}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Players */}
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-purple-500 mt-0.5" />
                <div className="space-y-2">
                  <p className="text-xs text-slate-500">
                    {t('registration.accessCodeEntry.players')} ({getModalityDisplay(codeData.modality)})
                  </p>
                  
                  {/* Player 1 */}
                  <div className={`flex items-center gap-2 p-2 rounded-md ${
                    codeData.playerPosition === 'player1' 
                      ? 'bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20' 
                      : 'bg-white'
                  }`}>
                    <span className="text-sm font-medium text-slate-700">
                      {codeData.player1Name}
                    </span>
                    {codeData.playerPosition === 'player1' && (
                      <Badge className="bg-primary text-primary-foreground text-xs">
                        {t('registration.accessCodeEntry.you')}
                      </Badge>
                    )}
                  </div>

                  {/* Player 2 (if doubles) */}
                  {codeData.player2Name && (
                    <div className={`flex items-center gap-2 p-2 rounded-md ${
                      codeData.playerPosition === 'player2' 
                        ? 'bg-gradient-to-r from-blue-100 to-green-100 border border-blue-200' 
                        : 'bg-white'
                    }`}>
                      <span className="text-sm font-medium text-slate-700">
                        {codeData.player2Name}
                      </span>
                      {codeData.playerPosition === 'player2' && (
                        <Badge className="bg-blue-500 text-white text-xs">
                          {t('registration.accessCodeEntry.you')}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Claim Button */}
            <Button
              onClick={handleClaim}
              disabled={isClaiming}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-medium py-3"
            >
              {isClaiming ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('registration.accessCodeEntry.claiming')}
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t('registration.accessCodeEntry.claimButton')}
                </>
              )}
            </Button>

            {/* Try Different Code */}
            <Button
              variant="ghost"
              onClick={() => {
                setCode('');
                clearError();
              }}
              className="w-full text-slate-600"
            >
              {t('registration.accessCodeEntry.useAnotherCode')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AccessCodeEntry;
